import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { alpha, Badge, Box, IconButton, Popover, Stack, Typography } from '@mui/material';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { useOpenVideoManagerModal } from 'pages/VideoManager/hooks/useOpenVideoManagerModal';
import { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatNotificationTimeAgo } from './formatNotificationTimeAgo';
import { NotificationListItem } from './NotificationListItem';
import { VideoGenerationNotificationItem, VideoNotificationItem } from './types';
import { getVideoNotificationAdPath, isVideoGenerationNotification } from './videoNotifications';
import { useVideoNotifications } from './VideoNotificationsContext';

export const NotificationBell: FC = () => {
  const navigate = useNavigate();
  const openVideoManager = useOpenVideoManagerModal();
  const { formatMessage } = useExtendedIntl();
  const { visibleItems, unreadCount, markAsRead, dismissNotification } = useVideoNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // A failed generation has no call to action to mark it read, so seeing it in the list does.
    visibleItems
      .filter((item) => isVideoGenerationNotification(item) && item.status === 'failed' && !item.read)
      .forEach((item) => markAsRead(item.id));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCtaClick = (id: string, path: string) => {
    markAsRead(id);
    handleClose();
    navigate(path);
  };

  const handleGeneratedVideoClick = (item: VideoGenerationNotificationItem) => {
    markAsRead(item.id);
    handleClose();
    openVideoManager('library', item.videoFileId ?? undefined, item.advertiserPrettyId);
  };

  // A failed generation only informs, so it has no call to action.
  const getCtaClickHandler = (item: VideoNotificationItem): (() => void) | undefined => {
    if (!isVideoGenerationNotification(item)) {
      return () => handleCtaClick(item.id, getVideoNotificationAdPath(item));
    }

    return item.status === 'ready' ? () => handleGeneratedVideoClick(item) : undefined;
  };

  const handleDismiss = (id: string): void => {
    (dismissNotification as (notificationId: string) => void)(id);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={formatMessage({ id: 'video-notifications.bell.aria-label' })}
        data-testid="header-notifications-button"
        sx={{
          color: 'inherit',
          ':hover': {
            backgroundColor: ({ palette }) => alpha(palette.common.white, 0.15),
          },
        }}
      >
        <Badge color="error" badgeContent={unreadCount} overlap="circular" data-testid="header-notifications-badge">
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        data-testid="header-notifications-popover"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ width: '24rem', maxWidth: 'calc(100vw - 2rem)' }}>
          <Typography variant="subtitle1" sx={{ px: 2, pt: 2, pb: 1 }}>
            {formatMessage({ id: 'shared.notifications' })}
          </Typography>
          {visibleItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ px: 2, pb: 2 }} data-testid="header-notifications-empty">
              {formatMessage({ id: 'video-notifications.inbox.empty' })}
            </Typography>
          ) : (
            <Stack component="ul" sx={{ maxHeight: '24rem', overflowY: 'auto', m: 0, p: 0 }}>
              {visibleItems.map((item) => {
                const onCtaClick = getCtaClickHandler(item);

                return (
                  <NotificationListItem
                    key={item.id}
                    id={item.id}
                    title={formatMessage({
                      id:
                        item.status === 'failed'
                          ? 'video-notifications.inbox.failed'
                          : 'video-notifications.inbox.ready',
                    })}
                    timeAgo={formatNotificationTimeAgo(item.updatedAt)}
                    unread={!item.read}
                    ctaLabel={onCtaClick ? formatMessage({ id: 'video-notifications.inbox.cta' }) : undefined}
                    dismissLabel={formatMessage({ id: 'video-notifications.inbox.dismiss' })}
                    onCtaClick={onCtaClick}
                    onDismiss={() => handleDismiss(item.id)}
                  />
                );
              })}
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
};
